import React, { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import { useLazyQuery, useLazyCommand, useEvent } from '@soap/modules';
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetClose } from '../ui/sheet';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Label } from '../ui/label';

const DEFAULT_AI_MODEL_KEY = 'gpt-5-mini';

type AvailableAiModel = { e138_Key?: string; e138_Value?: string };

type E138Profile = {
  e138_PreferredAiModelKey?: string | null;
  e138_AvailableAiModels?: AvailableAiModel[];
};

export function DebugOptionsDrawer() {
  const [open, setOpen] = useState(false);
  const [getProfile, e138GotProfile] = useLazyQuery('C166v1_GetUserProfile', null);
  const [setPreferredAiModel] = useLazyCommand('C301v1_SetPreferredAiModel');
  const [selectedKey, setSelectedKey] = useState<string>(DEFAULT_AI_MODEL_KEY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && !e138GotProfile) {
      getProfile();
    }
  }, [open, e138GotProfile, getProfile]);

  useEffect(() => {
    if (e138GotProfile) {
      const profile = e138GotProfile as E138Profile;
      const preferred = profile.e138_PreferredAiModelKey ?? DEFAULT_AI_MODEL_KEY;
      setSelectedKey(preferred);
    }
  }, [e138GotProfile]);

  useEvent({
    eventName: 'E300v1_PreferredAiModelUpdated',
    onEventReceived: (event: { e300_PreferredAiModelKey?: string }) => {
      if (event?.e300_PreferredAiModelKey) {
        setSelectedKey(event.e300_PreferredAiModelKey);
      }
    },
  });

  const profile = e138GotProfile as E138Profile | undefined;
  const availableModels = profile?.e138_AvailableAiModels ?? [];

  const handleModelChange = async (key: string) => {
    setSelectedKey(key);
    setSaving(true);
    try {
      await setPreferredAiModel({ C301_AiModelKey: key });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-action-blue focus:ring-offset-2"
          aria-label="Open debug options"
        >
          <Settings className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" dismissOnOverlayClick={false} className="flex w-full flex-col sm:max-w-md">
        <SheetClose />
        <SheetHeader>
          <SheetTitle>Debug options</SheetTitle>
        </SheetHeader>
        <div className="px-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="ai-model-select">AI Model</Label>
            <Select
              value={selectedKey}
              onValueChange={handleModelChange}
              disabled={saving || availableModels.length === 0}
            >
              <SelectTrigger id="ai-model-select" className="w-full">
                <SelectValue placeholder="Select model..." />
              </SelectTrigger>
              <SelectContent>
                {availableModels.map((m) => {
                  const key = m.e138_Key ?? '';
                  const value = m.e138_Value ?? key;
                  return (
                    <SelectItem key={key} value={key}>
                      {value}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {saving && (
              <p className="text-xs text-gray-500">Saving...</p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
